import {
  Component,
  ChangeDetectorRef,
  OnInit,
  ViewChild,
  inject,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { BaseListingComponent } from '../../components/_common/base-listing/base-listing.component'
import { CoreService } from '../../services'
import { purchaseOrdersColumns } from '../../utils/definitions.util'
import { DepotService } from '../../services/entity-services/depot.service'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { SupplierService } from '../../services/entity-services/supplier.services'
import { TagService } from '../../services/entity-services/tag.service'
import { PurchaseOrderService } from '../../services/entity-services/purchaseService.service'

const DEFAULT_FILTERS = {
  status: '',
  depot: null,
  payments: [],
  dateCreatedFrom: '',
  dateCreatedTo: '',
  supplier: null,
  dateEstimatedArrivalFrom: '',
  dateEstimatedArrivalTo: '',
  fulltext: '',
  tags: [],
  tagsNegate: [],
}

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',
  styleUrls: ['./purchase-orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PurchaseOrdersComponent extends BaseListing implements OnInit {
  private depotService = inject(DepotService)
  private tagService = inject(TagService)
  private supplierService = inject(SupplierService)
  private purchaseOrderService = inject(PurchaseOrderService)

  @ViewChild('baseListingComponent', { static: true }) baseListingComponent: BaseListingComponent

  listingColumns = signal(purchaseOrdersColumns)

  sortProp = signal('created')
  sortDir = signal('desc')

  filters = signal(structuredClone(DEFAULT_FILTERS))
  filtersExpanded = signal(false)
  readonly defaultFilters = DEFAULT_FILTERS
  readonly advancedFilterKeys = ['dateEstimatedArrivalFrom', 'dateEstimatedArrivalTo', 'tags', 'tagsNegate', 'status']

  statusOptions = ['all', 'new', 'partly_stocked', 'stocked']

  userHavePermissionForThisPage = false
  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_ORDER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryTagsOptions = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name', direction: 'asc' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'purchase_order'),
  }))

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers({ sort: 'name', direction: 'asc' }, { id: null, name: null })
        .then(result => result.items)
    },
  }))

  depotsOptions = computed(() =>
    this.queryDepotsOptions.data()?.length ? ['all', ...this.queryDepotsOptions.data()] : ['all']
  )

  suppliersOptions = computed(() =>
    this.querySuppliers.data()?.length ? ['all', ...this.querySuppliers.data()] : ['all']
  )

  readonly queryFilters = computed(() => {
    const f0 = this.filters()
    const q: any = {
      depot: f0.depot || undefined,
      supplier: f0.supplier || undefined,
      fulltext: f0.fulltext?.length ? f0.fulltext.trim() : undefined,
      status: f0.status?.length ? f0.status.trim() : undefined,
      tags: f0.tags?.length ? f0.tags : undefined,
      tagsNegate: f0.tagsNegate?.length ? f0.tagsNegate : undefined,
    }

    Object.keys(q).forEach(k => q[k] === undefined && delete q[k])

    if (f0.dateCreatedFrom?.length || f0.dateCreatedTo?.length) {
      q.created = {
        ...(f0.dateCreatedFrom?.length ? { left_date: f0.dateCreatedFrom } : {}),
        ...(f0.dateCreatedTo?.length ? { right_date: f0.dateCreatedTo + 'T23:59:59' } : {}),
      }
    }

    if (f0.dateEstimatedArrivalFrom?.length || f0.dateEstimatedArrivalTo?.length) {
      q.dateEstimatedArrival = {
        ...(f0.dateEstimatedArrivalFrom?.length ? { left_date: f0.dateEstimatedArrivalFrom } : {}),
        ...(f0.dateEstimatedArrivalTo?.length ? { right_date: f0.dateEstimatedArrivalTo + 'T23:59:59' } : {}),
      }
    }

    return q
  })

  readonly queryPurchaseOrders = injectInfiniteQuery(() => ({
    queryKey: ['purchaseOrders', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      return await this.purchaseOrderService
        .getPurchaseOrders(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          {
            id: null,
            number: null,
            status: { id: null, name: null },
            createdBy: { name: null },
            tags: { name: null },
            depot: { name: null },
            supplier: { name: null },
            created: null,
            externalReference: null,
            purchasePriceAll: null,
            dateEstimatedArrival: null,
            supplierReference: null,
          }
        )
        .then(result => {
          this.saveViewSettings('purchaseOrders', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  areCoreDataLoading = computed(() => {
    return (
      this.queryDepotsOptions.isFetching() || this.queryTagsOptions.isFetching() || this.querySuppliers.isFetching()
    )
  })

  totalItems = computed(() => this.queryPurchaseOrders.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  loading = computed(
    () =>
      this.queryPurchaseOrders.isPending() ||
      this.queryPurchaseOrders.isFetching() ||
      this.querySuppliers.isPending() ||
      this.querySuppliers.isFetching() ||
      this.queryDepotsOptions.isPending() ||
      this.queryDepotsOptions.isFetching() ||
      this.queryTagsOptions.isPending() ||
      this.queryTagsOptions.isFetching()
  )

  constructor(
    protected core: CoreService,
    protected changeDetector: ChangeDetectorRef
  ) {
    super(core)
    effect(() => {
      this.queryDepotsOptions.data()
      this.querySuppliers.data()
      this.queryTagsOptions.data()
      this.queryPurchaseOrders.data()
    })
    this.userHavePermissionForThisPage = this.core.services.user.user.company.moduleRelations?.some(
      moduleRelation => moduleRelation.module.id === 'purchase-orders'
    )
  }

  async ngOnInit() {
    if (!this.userHavePermissionForThisPage) return
    const view = this.loadViewSettings('purchaseOrders')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'created')
    this.sortDir.set(view?.sortDir || 'desc')
    this.loadSorting('purchaseOrders')
  }

  setFilter(key: string, value: any) {
    this.filters.update(f => ({ ...f, [key]: value }))
  }

  public clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryPurchaseOrders.refetch()
  }

  protected readonly DEFAULT_FILTERS = DEFAULT_FILTERS
}
